import { useCallback } from 'react'

import { useAuth } from '@/context/authContext'
import { useLocale } from '@/context/localeContext'
import { useNavigation } from '@/context/navigationContext'
import { useProject } from '@/context/projectContext'
import { useTimezonedDates } from '@/context/timezoneContext'
import { useUser } from '@/context/userContext'

export type FeedbackInfo = {
  // User Context
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    roles: string[]
  }
  builder: {
    id: string
    company: string
    subscription: string
  }

  // Project Context
  project?: {
    id: string
    name: string
    status: string
  }

  // Location Context
  navigation: {
    currentPath: string
    currentNavItem?: {
      id: string
      name: string
      path: string
    }
  }

  // Locale/Timezone Context
  location: {
    country: string
    language: string
    locale: string
    timezone: string
    userTimezone: string
  }

  // Browser Context
  browser: {
    userAgent: string
    viewport: {
      width: number
      height: number
    }
  }
}

export const useGatherFeedback = () => {
  const { user, builder } = useUser()
  const { roles } = useAuth()
  const { project } = useProject()
  const { locale, language, countryCode } = useLocale()
  const { userTimezone, projectTimezone } = useTimezonedDates()
  const { currentNavItem } = useNavigation()

  const getFeedbackInfo = useCallback(
    (): FeedbackInfo => ({
      user: {
        id: user?.id,
        email: user?.email,
        firstName: user?.firstName,
        lastName: user?.lastName,
        roles,
      },
      builder: {
        id: builder?.id,
        company: builder?.company?.name,
        subscription: builder?.subscription,
      },
      project: project && {
        id: project.id,
        name: project.name,
        status: project.status,
      },
      navigation: {
        currentPath: window.location.pathname,
        currentNavItem: currentNavItem && {
          id: currentNavItem.id,
          name: currentNavItem.title,
          path: currentNavItem.path,
        },
      },
      location: {
        country: countryCode,
        language,
        locale,
        timezone: projectTimezone,
        userTimezone,
      },
      browser: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      },
    }),
    [
      user,
      builder,
      roles,
      project,
      locale,
      language,
      countryCode,
      userTimezone,
      projectTimezone,
      currentNavItem,
    ]
  )

  if (typeof window === 'undefined') {
    return { getFeedbackInfo: null }
  }

  return { getFeedbackInfo }
}
